/**
 * Post-generation swap picker.
 *
 * Replaces the old `showPrompt`-based flow in `handleSwap()`. Presents a
 * rich candidate list (workload + badges + rejection reasons), a segmented
 * free/trade tab, filter + sort controls, a live preview panel driven by
 * `engine.previewSwap()` / `engine.previewSwapChain()`, and a confirm step.
 *
 * On commit the caller's `onCommit` handler fires with the pre-swap
 * assignment snapshot so the app-level undo stack can record it.
 */

import type { SwapPreview } from '../engine/scheduler';
import {
  type CandidateEligibility,
  getCandidatesWithEligibility,
  getEligibleParticipantsForSlot,
} from '../engine/validator';
import {
  type Assignment,
  AssignmentStatus,
  Level,
  type Participant,
  type ParticipantCapacity,
  type Schedule,
  type SchedulingEngine,
  type Task,
} from '../index';
import { computeAllCapacities } from '../utils/capacity';
import { renderParticipantCard } from './participant-card';
import { isTouchDevice } from './responsive';
import { violationLabel } from './schedule-utils';
import { escAttr, escHtml, fmt, stripDayPrefix } from './ui-helpers';
import { showBottomSheet, showToast } from './ui-modal';
import { computeWeeklyWorkloads, getCapacityWindow, type WeeklyWorkload } from './workload-utils';

/** Optional record-as-future-unavailability entry built when the user kept
 *  the (default-on) "mark vacated participant unavailable for this slot's time"
 *  checkbox checked. App.ts upserts each entry onto
 *  `Schedule.scheduleUnavailability` after the undo snapshot push. */
export interface VacatedSlotRecord {
  participantId: string;
  start: Date;
  end: Date;
  /** Optional free-text reason captured from the picker input (free mode only). */
  reason?: string;
}

export interface SwapPickerDeps {
  engine: SchedulingEngine;
  schedule: Schedule;
  disabledHC: Set<string>;
  restRuleMap: Map<string, number>;
  dayStartHour: number;
  /**
   * Called after the engine successfully commits the swap. The picker
   * passes the pre-commit assignments snapshot so the caller can push
   * it onto the unified undo stack. `recordVacatedSlots` is non-empty when
   * the user kept the "mark vacated participant unavailable" checkbox
   * checked — free mode produces 1 entry (source), trade mode produces 2
   * (source + trade target). App.ts upserts these entries onto
   * `Schedule.scheduleUnavailability` after the undo snapshot push.
   */
  onCommit: (info: {
    label: string;
    preCommitAssignments: Assignment[];
    swappedAssignmentIds: string[];
    recordVacatedSlots: VacatedSlotRecord[];
  }) => void;
}

type PickerMode = 'free' | 'trade';
type PickerSort = 'eligibility' | 'workload-asc' | 'workload-desc' | 'name';

/** Session-scoped default for the "record vacated participant as
 *  future-unavailable" checkbox. Updated whenever the user toggles it; the
 *  next swap-picker open inherits the last choice. */
let _swapRecordVacatedDefault = true;

interface PickerState {
  assignmentId: string;
  mode: PickerMode;
  filter: {
    text: string;
    group: string | null;
    level: Level | null;
  };
  sort: PickerSort;
  selectedCandidateId: string | null;
  selectedTradeAssignmentId: string | null;
  preview: SwapPreview | null;
  committing: boolean;
  /** Free-mode reason input value, preserved across re-renders. */
  reason: string;
}

interface ResolvedContext {
  sourceAssignment: Assignment;
  sourceTask: Task;
  sourceParticipant: Participant;
  candidates: CandidateEligibility[];
  tradeCandidates: TradeCandidate[];
  baselineWorkloads: Map<string, WeeklyWorkload>;
  capacities: Map<string, ParticipantCapacity>;
  /** Reference to the full task list — needed for post-swap workload recompute. */
  tasks: Task[];
  /** Reference to the full participant list — needed for affected-participant lookups. */
  participants: Participant[];
}

interface TradeCandidate {
  participant: Participant;
  assignment: Assignment;
  task: Task;
  /** Hebrew label like "הדליה — בוקר" */
  targetLabel: string;
}

/**
 * Open the swap picker as a bottom sheet. The sheet is used on both
 * mobile and desktop to keep the layout consistent; CSS media queries
 * handle visual differences.
 */
export async function openSwapPicker(assignmentId: string, deps: SwapPickerDeps): Promise<void> {
  const state: PickerState = {
    assignmentId,
    mode: 'free',
    filter: { text: '', group: null, level: null },
    sort: 'eligibility',
    selectedCandidateId: null,
    selectedTradeAssignmentId: null,
    preview: null,
    committing: false,
    reason: '',
  };

  const ctx = resolveContext(state, deps);
  if (!ctx) {
    showToast('לא ניתן לפתוח החלפה — השיבוץ אינו זמין.', { type: 'error' });
    return;
  }

  const sheet = showBottomSheet(renderBody(state, ctx), {
    title: `החלפת משתתף — ${escHtml(cleanTaskName(ctx.sourceTask))}`,
    onClose: () => {
      if (_swapTooltipEl) _swapTooltipEl.style.display = 'none';
    },
  });
  sheet.el.classList.add('swap-picker-sheet');

  // After the sheet is painted, wire up events.
  requestAnimationFrame(() => {
    wireEvents(sheet.el, state, ctx, deps, () => sheet.close());
  });
}

/* ──────────────────────────────────────────────────────────────────────────
 * Context resolution (runs once per open)
 * ──────────────────────────────────────────────────────────────────────── */

function resolveContext(state: PickerState, deps: SwapPickerDeps): ResolvedContext | null {
  const { engine, schedule, disabledHC, restRuleMap, dayStartHour } = deps;
  const sourceAssignment = schedule.assignments.find((a) => a.id === state.assignmentId);
  if (!sourceAssignment || sourceAssignment.status === AssignmentStatus.Frozen) return null;

  const sourceTask = schedule.tasks.find((t) => t.id === sourceAssignment.taskId);
  if (!sourceTask) return null;

  const sourceParticipant = schedule.participants.find((p) => p.id === sourceAssignment.participantId);
  if (!sourceParticipant) return null;

  // Schedule-scoped Future-SOS unavailability windows — layer on top of
  // participant master availability so focal participants don't surface as
  // candidates for slots that fall within their unavailability window.
  const extraUnavailability = schedule.scheduleUnavailability;
  // Schedule-scoped capability-change losses — layer on top of participant
  // master-data certifications so a cert-lost participant is not surfaced as
  // eligible for a cert-gated slot (HC-2), and a participant whose forbidden
  // cert was lost is no longer falsely rejected (HC-11). Mirrors the
  // apply-time validator (engine.validate threads schedule.capabilityLoss),
  // keeping the candidate-list badge honest with the preview/commit gate.
  const extraCapabilityLoss = schedule.capabilityLoss;
  const scheduleContext = engine.getScheduleContext();

  // Candidates: every participant with eligibility + rejection reason
  const candidates = getCandidatesWithEligibility(
    sourceTask,
    sourceAssignment.slotId,
    schedule.participants,
    schedule.assignments,
    schedule.tasks,
    disabledHC,
    restRuleMap,
    extraUnavailability,
    scheduleContext,
    extraCapabilityLoss,
  ).filter((c) => c.participant.id !== sourceParticipant.id);

  // Trade candidates: for each OTHER assignment, check if the outgoing
  // participant can take that slot AND the other assignment's participant
  // is eligible for our source slot.
  const tradeCandidates = computeTradeCandidates(
    schedule,
    sourceAssignment,
    sourceTask,
    sourceParticipant,
    disabledHC,
    restRuleMap,
    extraUnavailability,
    scheduleContext,
    extraCapabilityLoss,
  );

  // Capacities + workloads (baseline — used for "before" numbers in preview).
  // Full-period window so the preview's utilization % matches the popup/sidebar/profile.
  const { start, end } = getCapacityWindow(schedule);
  const capacities = computeAllCapacities(schedule.participants, start, end, dayStartHour);
  const baselineWorkloads = computeWeeklyWorkloads(
    schedule.participants,
    schedule.assignments,
    schedule.tasks,
    capacities,
  );

  return {
    sourceAssignment,
    sourceTask,
    sourceParticipant,
    candidates,
    tradeCandidates,
    baselineWorkloads,
    capacities,
    tasks: schedule.tasks,
    participants: schedule.participants,
  };
}

function computeTradeCandidates(
  schedule: Schedule,
  sourceAssignment: Assignment,
  sourceTask: Task,
  sourceParticipant: Participant,
  disabledHC: Set<string>,
  restRuleMap: Map<string, number>,
  extraUnavailability: Array<{ participantId: string; start: Date; end: Date }> | undefined,
  scheduleContext: import('../shared/utils/time-utils').ScheduleContext | undefined,
  extraCapabilityLoss: Schedule['capabilityLoss'],
): TradeCandidate[] {
  // Eligible participants for the source slot (used as filter A).
  const eligibleForSource = new Set(
    getEligibleParticipantsForSlot(
      sourceTask,
      sourceAssignment.slotId,
      schedule.participants,
      schedule.assignments,
      schedule.tasks,
      disabledHC,
      restRuleMap,
      extraUnavailability,
      scheduleContext,
      extraCapabilityLoss,
    ).map((p) => p.id),
  );

  const trades: TradeCandidate[] = [];
  for (const candidate of schedule.assignments) {
    if (candidate.id === sourceAssignment.id) continue;
    if (candidate.participantId === sourceParticipant.id) continue;
    if (candidate.status === AssignmentStatus.Frozen) continue;
    if (!eligibleForSource.has(candidate.participantId)) continue;

    const candTask = schedule.tasks.find((t) => t.id === candidate.taskId);
    if (!candTask) continue;

    // Filter B: the source participant must be eligible for the candidate's slot
    const eligibleForCandidateSlot = new Set(
      getEligibleParticipantsForSlot(
        candTask,
        candidate.slotId,
        schedule.participants,
        schedule.assignments,
        schedule.tasks,
        disabledHC,
        restRuleMap,
        extraUnavailability,
        scheduleContext,
        extraCapabilityLoss,
      ).map((p) => p.id),
    );
    if (!eligibleForCandidateSlot.has(sourceParticipant.id)) continue;

    const candParticipant = schedule.participants.find((p) => p.id === candidate.participantId);
    if (!candParticipant) continue;

    const slot = candTask.slots.find((s) => s.slotId === candidate.slotId);
    const slotLabel = slot?.label ? ` — ${slot.label}` : '';
    // U+2066 LRI + U+2069 PDI force the time range into an LTR isolate so the
    // two HH:mm runs don't get flipped by the surrounding RTL context after
    // the label is passed through escHtml.
    const timeRange = `\u2066${fmt(candTask.timeBlock.start)}–${fmt(candTask.timeBlock.end)}\u2069`;
    const targetLabel = `${cleanTaskName(candTask)}${slotLabel} (${timeRange})`;

    trades.push({
      participant: candParticipant,
      assignment: candidate,
      task: candTask,
      targetLabel,
    });
  }
  return trades;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Rendering
 * ──────────────────────────────────────────────────────────────────────── */

function renderBody(state: PickerState, ctx: ResolvedContext): string {
  const header = renderHeader(state, ctx);
  const tabs = renderTabs(state);
  const filters = renderFilters(state, ctx);
  const list = state.mode === 'free' ? renderCandidateList(state, ctx) : renderTradeList(state, ctx);
  const preview = renderPreviewPanel(state, ctx);
  const actions = renderActions(state, ctx);
  // Preview + actions are wrapped in a single sticky footer so the preview
  // panel sits directly above the action bar without two elements fighting
  // for `position: sticky; bottom: 0`.
  return `<div class="swap-picker">
    ${header}
    ${tabs}
    ${filters}
    <div class="swap-picker-list" data-mode="${state.mode}">${list}</div>
    <div class="swap-picker-footer">
      ${preview}
      ${actions}
    </div>
  </div>`;
}

function renderHeader(_state: PickerState, ctx: ResolvedContext): string {
  const { sourceTask, sourceAssignment, sourceParticipant } = ctx;
  const slot = sourceTask.slots.find((s) => s.slotId === sourceAssignment.slotId);
  const slotLabel = slot?.label ? ` — ${slot.label}` : '';
  const timeRange = `<span dir="ltr">${fmt(sourceTask.timeBlock.start)} – ${fmt(sourceTask.timeBlock.end)}</span>`;
  return `<div class="swap-picker-header">
    <div class="swap-picker-title">${escHtml(cleanTaskName(sourceTask))}${escHtml(slotLabel)}</div>
    <div class="swap-picker-sub">${timeRange} · נוכחי: <strong>${escHtml(sourceParticipant.name)}</strong></div>
  </div>`;
}

function renderTabs(state: PickerState): string {
  return `<div class="swap-picker-tabs" role="tablist">
    <button class="swap-picker-tab ${state.mode === 'free' ? 'swap-picker-tab-active' : ''}" data-mode="free" role="tab">שיבוץ חדש</button>
    <button class="swap-picker-tab ${state.mode === 'trade' ? 'swap-picker-tab-active' : ''}" data-mode="trade" role="tab">החלפה הדדית</button>
  </div>`;
}

function renderFilters(state: PickerState, ctx: ResolvedContext): string {
  if (state.mode === 'trade') {
    // Trade mode: only text filter (groups/levels are less meaningful here)
    return `<div class="swap-picker-filters">
      <input type="search" class="swap-picker-search" id="swap-picker-search" placeholder="🔍 חפש..." value="${escHtml(state.filter.text)}" autocomplete="off" />
    </div>`;
  }

  const groupChips = uniqueGroups(ctx.candidates)
    .map(
      (g) =>
        `<button class="swap-picker-chip ${state.filter.group === g ? 'chip-active' : ''}" data-filter-group="${escHtml(g)}">${escHtml(g)}</button>`,
    )
    .join('');

  const levelChips = [Level.L0, Level.L2, Level.L3, Level.L4]
    .map(
      (lv) =>
        `<button class="swap-picker-chip ${state.filter.level === lv ? 'chip-active' : ''}" data-filter-level="${lv}">L${lv}</button>`,
    )
    .join('');

  return `<div class="swap-picker-filters">
    <input type="search" class="swap-picker-search" id="swap-picker-search" placeholder="🔍 חפש..." value="${escHtml(state.filter.text)}" autocomplete="off" />
    <select class="swap-picker-sort" id="swap-picker-sort">
      <option value="eligibility" ${state.sort === 'eligibility' ? 'selected' : ''}>מיון: זמינות</option>
      <option value="workload-asc" ${state.sort === 'workload-asc' ? 'selected' : ''}>מיון: עומס ↑</option>
      <option value="workload-desc" ${state.sort === 'workload-desc' ? 'selected' : ''}>מיון: עומס ↓</option>
      <option value="name" ${state.sort === 'name' ? 'selected' : ''}>מיון: שם</option>
    </select>
    <div class="swap-picker-chips">
      ${groupChips}
      <span class="swap-picker-chip-sep"></span>
      ${levelChips}
    </div>
  </div>`;
}

function renderCandidateList(state: PickerState, ctx: ResolvedContext): string {
  // Apply text / group / level filters to the full candidate pool
  const txt = state.filter.text.trim().toLowerCase();
  let candidates = ctx.candidates;
  if (txt) candidates = candidates.filter((c) => c.participant.name.toLowerCase().includes(txt));
  if (state.filter.group) candidates = candidates.filter((c) => c.participant.group === state.filter.group);
  if (state.filter.level !== null) candidates = candidates.filter((c) => c.participant.level === state.filter.level);

  // Empty-eligible fallback: if none of the filtered candidates are eligible,
  // render ALL filtered rows (greyed, with rejection reasons) so the user
  // can diagnose why nobody fits. Otherwise hide ineligible rows to keep
  // the list focused on actionable choices.
  const eligible = candidates.filter((c) => c.eligible);
  const showAllAsDiagnostic = eligible.length === 0 && candidates.length > 0;
  const display = showAllAsDiagnostic ? candidates : eligible;

  // Sort
  const sorted = sortCandidates([...display], state.sort, ctx.baselineWorkloads);

  if (sorted.length === 0) {
    return `<div class="swap-picker-empty">אין תוצאות לסינון זה.</div>`;
  }

  const banner = showAllAsDiagnostic
    ? `<div class="swap-picker-empty-banner">אין מועמדים זמינים להחלפה. מוצגים כל המשתתפים עם סיבת הדחייה לכל אחד.</div>`
    : '';

  const cards = sorted
    .map((c) => {
      const wl = ctx.baselineWorkloads.get(c.participant.id);
      return renderParticipantCard({
        participant: c.participant,
        eligible: c.eligible,
        rejectionReason: c.reason,
        workload: wl,
        capacity: ctx.capacities.get(c.participant.id),
        // Badge shows effective hours so the corner number matches the
        // sorting criterion (workload sorts use `effectiveHours`). Keeping
        // these aligned prevents the puzzling "list ordered by one metric,
        // badge shows another" mismatch.
        loadBadge: wl ? loadBadgeForWorkload(wl) : undefined,
        selected: state.selectedCandidateId === c.participant.id,
        extraClass: 'warehouse-card-sheet',
      });
    })
    .join('');

  return banner + cards;
}

function renderTradeList(state: PickerState, ctx: ResolvedContext): string {
  let trades = ctx.tradeCandidates;
  const txt = state.filter.text.trim().toLowerCase();
  if (txt) {
    trades = trades.filter(
      (t) => t.participant.name.toLowerCase().includes(txt) || t.targetLabel.toLowerCase().includes(txt),
    );
  }

  if (trades.length === 0) {
    return `<div class="swap-picker-empty">אין שיבוצים שניתן להחליף איתם (נדרשת זכאות הדדית למשבצות).</div>`;
  }

  return trades
    .map((tc) => {
      const wl = ctx.baselineWorkloads.get(tc.participant.id);
      const card = renderParticipantCard({
        participant: tc.participant,
        eligible: true,
        workload: wl,
        capacity: ctx.capacities.get(tc.participant.id),
        loadBadge: wl ? loadBadgeForWorkload(wl) : undefined,
        selected: state.selectedTradeAssignmentId === tc.assignment.id,
        extraClass: 'warehouse-card-sheet swap-trade-card',
        extraDataAttrs: `data-trade-assignment-id="${tc.assignment.id}"`,
      });
      return `<div class="swap-trade-row">
        ${card}
        <div class="swap-trade-target">${escHtml(tc.targetLabel)}</div>
      </div>`;
    })
    .join('');
}

function renderPreviewPanel(state: PickerState, ctx: ResolvedContext): string {
  if (!state.preview) {
    return `<div class="swap-preview-panel swap-preview-empty">בחר/י מועמד כדי לראות השפעת ההחלפה.</div>`;
  }

  const preview = state.preview;

  // Recompute post-swap workloads from the simulated assignments snapshot
  // so the UI can show the "after" numbers without re-mutating the engine.
  const postWorkloads = computeWeeklyWorkloads(
    getAffectedParticipants(ctx, preview),
    preview.simulatedAssignments,
    ctx.tasks,
    ctx.capacities,
  );

  const deltaRows = preview.affectedParticipantIds
    .map((pid) => {
      const p = getParticipantById(ctx, pid);
      if (!p) return '';
      const before = ctx.baselineWorkloads.get(pid);
      const after = postWorkloads.get(pid);
      if (!before || !after) return '';
      return renderDeltaRow(p, before, after);
    })
    .join('');

  const violationsHtml = preview.violations.length
    ? `<ul class="swap-preview-violations">${preview.violations
        .map((v) => `<li dir="rtl"><code>${violationLabel(v.code)}</code> · ${escHtml(v.message)}</li>`)
        .join('')}</ul>`
    : '';

  const addedHtml = preview.addedSoftWarnings.length
    ? `<div class="swap-preview-soft swap-preview-soft-added">
        <div class="swap-preview-soft-label">אזהרות חדשות:</div>
        <ul>${preview.addedSoftWarnings.map((w) => `<li dir="rtl"><code>${violationLabel(w.code)}</code> · ${escHtml(w.message)}</li>`).join('')}</ul>
      </div>`
    : '';
  const removedHtml = preview.removedSoftWarnings.length
    ? `<div class="swap-preview-soft swap-preview-soft-removed">
        <div class="swap-preview-soft-label">אזהרות שבוטלו:</div>
        <ul>${preview.removedSoftWarnings.map((w) => `<li dir="rtl"><code>${violationLabel(w.code)}</code> · ${escHtml(w.message)}</li>`).join('')}</ul>
      </div>`
    : '';

  const statusBadge = preview.valid
    ? `<span class="swap-preview-ok">תקין</span>`
    : `<span class="swap-preview-bad">לא תקין</span>`;

  return `<div class="swap-preview-panel ${preview.valid ? '' : 'swap-preview-invalid'}">
    <div class="swap-preview-header">תצוגה מקדימה ${statusBadge}</div>
    <div class="swap-preview-deltas">${deltaRows}</div>
    ${violationsHtml}
    ${addedHtml}
    ${removedHtml}
  </div>`;
}

function renderDeltaRow(p: Participant, before: WeeklyWorkload, after: WeeklyWorkload): string {
  const deltaEff = after.effectiveHours - before.effectiveHours;
  const sign = deltaEff > 0 ? '+' : '';
  const deltaClass = deltaEff > 0 ? 'swap-delta-up' : deltaEff < 0 ? 'swap-delta-down' : 'swap-delta-same';
  // dir="ltr" on the values span keeps "before → after" in logical order
  // (arrow pointing with reading flow) regardless of the RTL parent. The
  // Hebrew "שע׳" inside remains strong-RTL and still renders correctly.
  return `<div class="swap-preview-delta-row">
    <span class="swap-preview-delta-name swap-delta-hover" data-pid="${p.id}">${escHtml(p.name)}</span>
    <span class="swap-preview-delta-values" dir="ltr">
      ${before.effectiveHours.toFixed(1)} → ${after.effectiveHours.toFixed(1)} שע׳
      <span class="${deltaClass}">(${sign}${deltaEff.toFixed(1)})</span>
    </span>
  </div>`;
}

function renderActions(state: PickerState, ctx: ResolvedContext): string {
  const canConfirm = state.preview?.valid && !state.committing;
  const recordChecked = _swapRecordVacatedDefault ? ' checked' : '';

  // Free mode: one checkbox for the outgoing source. Trade mode: two
  // independent checkboxes (one per participant) so the user can choose
  // to mark either, both, or neither side as future-unavailable.
  let recordRows: string;
  if (state.mode === 'trade' && state.selectedTradeAssignmentId) {
    const other = ctx.tradeCandidates.find((tc) => tc.assignment.id === state.selectedTradeAssignmentId);
    const otherName = other?.participant.name ?? '';
    recordRows = `<label class="swap-picker-record-vacated">
      <input type="checkbox" id="swap-picker-record-vacated"${recordChecked}>
      <span>סמן את <strong>${escHtml(ctx.sourceParticipant.name)}</strong> כלא־זמין/ה לחלון הזמן של המשבצת (לצורך החלפה עתידית)</span>
    </label>
    <label class="swap-picker-record-vacated">
      <input type="checkbox" id="swap-picker-record-vacated-other"${recordChecked}>
      <span>סמן את <strong>${escHtml(otherName)}</strong> כלא־זמין/ה לחלון הזמן של המשבצת (לצורך החלפה עתידית)</span>
    </label>`;
  } else {
    recordRows = `<label class="swap-picker-record-vacated">
      <input type="checkbox" id="swap-picker-record-vacated"${recordChecked}>
      <span>סמן את <strong>${escHtml(ctx.sourceParticipant.name)}</strong> כלא־זמין/ה לחלון הזמן של המשבצת (לצורך החלפה עתידית)</span>
    </label>`;
  }

  // Reason input is shown only in free mode. Trade-mode entries are
  // intentionally recorded without a `reason` — that behavior is under
  // user reconsideration and we don't want to bake reason capture into it.
  const reasonDisplay = _swapRecordVacatedDefault ? 'block' : 'none';
  const reasonRow =
    state.mode === 'free'
      ? `<div class="swap-picker-record-reason" id="swap-picker-record-reason" style="display: ${reasonDisplay};">
          <input id="swap-picker-record-reason-input"
                 class="input-sm swap-picker-record-reason-input"
                 type="text" maxlength="80" autocomplete="off"
                 placeholder="סיבה (אופציונלי, למשל: מילואים, חופש, מחלה)"
                 value="${escAttr(state.reason)}" />
        </div>`
      : '';

  return `<div class="swap-picker-actions">
    ${recordRows}
    ${reasonRow}
    <div class="swap-picker-actions-buttons">
      <button class="btn-outline swap-picker-cancel">ביטול</button>
      <button class="btn-primary swap-picker-confirm" ${canConfirm ? '' : 'disabled'}>אישור החלפה</button>
    </div>
  </div>`;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Event wiring & re-render
 * ──────────────────────────────────────────────────────────────────────── */

function wireEvents(
  root: HTMLElement,
  state: PickerState,
  ctx: ResolvedContext,
  deps: SwapPickerDeps,
  close: () => void,
): void {
  const rerender = (): void => {
    const body = root.querySelector('.gm-bs-body');
    if (!body) return;
    body.innerHTML = renderBody(state, ctx);
    wireEvents(root, state, ctx, deps, close);
  };

  // Tab switching
  root.querySelectorAll('.swap-picker-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      const mode = (btn as HTMLElement).dataset.mode as PickerMode;
      if (state.mode === mode) return;
      state.mode = mode;
      state.selectedCandidateId = null;
      state.selectedTradeAssignmentId = null;
      state.preview = null;
      rerender();
    });
  });

  // Search input
  const search = root.querySelector<HTMLInputElement>('#swap-picker-search');
  if (search) {
    search.addEventListener('input', () => {
      state.filter.text = search.value;
      // Only re-render the list + preview, keep focus.
      const list = root.querySelector('.swap-picker-list');
      if (list) {
        list.innerHTML = state.mode === 'free' ? renderCandidateList(state, ctx) : renderTradeList(state, ctx);
        wireCardClicks(root, state, ctx, deps, rerender);
      }
    });
  }

  // Sort dropdown
  const sortSel = root.querySelector<HTMLSelectElement>('#swap-picker-sort');
  if (sortSel) {
    sortSel.addEventListener('change', () => {
      state.sort = sortSel.value as PickerSort;
      rerender();
    });
  }

  // Chip filters (groups / levels)
  root.querySelectorAll('[data-filter-group]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const g = (btn as HTMLElement).dataset.filterGroup ?? '';
      state.filter.group = state.filter.group === g ? null : g;
      rerender();
    });
  });
  root.querySelectorAll('[data-filter-level]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const lv = Number((btn as HTMLElement).dataset.filterLevel ?? '0') as Level;
      state.filter.level = state.filter.level === lv ? null : lv;
      rerender();
    });
  });

  // Card clicks (free mode) + trade-row clicks (trade mode)
  wireCardClicks(root, state, ctx, deps, rerender);

  // Preview delta-row hover/tap → show surrounding tasks for that participant
  wireDeltaHover(root, state, ctx, deps);

  // Record-vacated checkbox: update session default whenever the user
  // toggles it so re-renders preserve the choice and the next picker open
  // inherits it. Also toggles the reason input visibility (free mode only —
  // the input is not rendered at all in trade mode).
  const recordCb = root.querySelector<HTMLInputElement>('#swap-picker-record-vacated');
  const recordCbOther = root.querySelector<HTMLInputElement>('#swap-picker-record-vacated-other');
  const reasonWrap = root.querySelector<HTMLElement>('#swap-picker-record-reason');
  const reasonInput = root.querySelector<HTMLInputElement>('#swap-picker-record-reason-input');
  if (recordCb) {
    recordCb.addEventListener('change', () => {
      _swapRecordVacatedDefault = recordCb.checked;
      if (reasonWrap) {
        reasonWrap.style.display = recordCb.checked ? 'block' : 'none';
        if (recordCb.checked && reasonInput) reasonInput.focus();
      }
    });
  }
  if (recordCbOther) {
    // Trade mode's per-participant checkboxes both feed the session default
    // (last toggle wins) so re-opens carry the user's most recent intent.
    recordCbOther.addEventListener('change', () => {
      _swapRecordVacatedDefault = recordCbOther.checked;
    });
  }
  if (reasonInput) {
    reasonInput.addEventListener('input', () => {
      state.reason = reasonInput.value;
    });
  }

  // Cancel / Confirm
  root.querySelector('.swap-picker-cancel')?.addEventListener('click', close);
  root.querySelector('.swap-picker-confirm')?.addEventListener('click', () => {
    commitSwap(state, ctx, deps, close, root);
  });
}

function wireDeltaHover(root: HTMLElement, state: PickerState, ctx: ResolvedContext, deps: SwapPickerDeps): void {
  const triggers = root.querySelectorAll<HTMLElement>('.swap-delta-hover');
  if (triggers.length === 0) return;

  const referenceTaskId = ctx.sourceTask.id;

  if (isTouchDevice) {
    // Remove any stale inline preview left from a prior render cycle.
    root.querySelector('.swap-inline-preview')?.remove();
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const pid = trigger.dataset.pid;
        if (!pid || !state.preview) return;
        const existing = root.querySelector('.swap-inline-preview');
        const alreadyOpen = existing?.previousElementSibling === trigger.parentElement;
        existing?.remove();
        if (alreadyOpen) return;

        const participant = getParticipantById(ctx, pid);
        if (!participant) return;
        const nextTasks = computePostSwapTasksForPreview(pid, state.preview, ctx.tasks, referenceTaskId);
        const detail = document.createElement('div');
        detail.className = 'swap-inline-preview rescue-hover-tt';
        detail.style.position = 'static';
        detail.style.width = 'auto';
        detail.style.marginTop = '4px';
        detail.innerHTML = buildSwapParticipantTooltip(
          participant.name,
          nextTasks,
          deps.schedule.periodStart,
          deps.dayStartHour,
        );
        trigger.parentElement?.insertAdjacentElement('afterend', detail);
      });
    });
    return;
  }

  const tooltip = getSwapTooltipEl();
  let hideTimer: ReturnType<typeof setTimeout> | null = null;
  const scheduleHide = (): void => {
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      tooltip.style.display = 'none';
    }, 120);
  };
  tooltip.addEventListener('mouseenter', () => {
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  });
  tooltip.addEventListener('mouseleave', () => {
    tooltip.style.display = 'none';
  });

  triggers.forEach((trigger) => {
    trigger.addEventListener('mouseenter', () => {
      const pid = trigger.dataset.pid;
      if (!pid || !state.preview) return;
      const participant = getParticipantById(ctx, pid);
      if (!participant) return;

      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }

      const nextTasks = computePostSwapTasksForPreview(pid, state.preview, ctx.tasks, referenceTaskId);
      tooltip.innerHTML = buildSwapParticipantTooltip(
        participant.name,
        nextTasks,
        deps.schedule.periodStart,
        deps.dayStartHour,
      );
      tooltip.style.display = 'block';

      const rect = trigger.getBoundingClientRect();
      const ttWidth = 260;
      const ttHeight = tooltip.offsetHeight || 140;
      // Preferred anchor: above the trigger, aligned to its right edge so the
      // tooltip flows away from the sticky footer rather than into it.
      let left = rect.right - ttWidth;
      let top = rect.top - ttHeight - 8;
      if (top < 4) top = rect.bottom + 8;
      if (left < 4) left = 4;
      if (left + ttWidth > window.innerWidth) left = window.innerWidth - ttWidth - 4;
      if (top + ttHeight > window.innerHeight) top = window.innerHeight - ttHeight - 4;

      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
    });
    trigger.addEventListener('mouseleave', scheduleHide);
  });
}

let _swapTooltipEl: HTMLElement | null = null;
function getSwapTooltipEl(): HTMLElement {
  if (_swapTooltipEl && document.body.contains(_swapTooltipEl)) return _swapTooltipEl;
  const el = document.createElement('div');
  // Reuse rescue styling so both surfaces look identical.
  el.className = 'rescue-hover-tt swap-hover-tt';
  el.style.display = 'none';
  document.body.appendChild(el);
  _swapTooltipEl = el;
  return el;
}

function wireCardClicks(
  root: HTMLElement,
  state: PickerState,
  ctx: ResolvedContext,
  deps: SwapPickerDeps,
  rerender: () => void,
): void {
  root.querySelectorAll('.swap-picker-list .warehouse-card').forEach((cardEl) => {
    cardEl.addEventListener('click', () => {
      const pid = (cardEl as HTMLElement).dataset.pid;
      const tradeId = (cardEl as HTMLElement).dataset.tradeAssignmentId;
      if (!pid) return;

      if (state.mode === 'trade' && tradeId) {
        state.selectedTradeAssignmentId = tradeId;
        state.selectedCandidateId = pid;
        state.preview = computeTradePreview(state, ctx, deps);
      } else if (state.mode === 'free') {
        // Ineligible candidates are non-interactive (their card is a diagnostic)
        const candidate = ctx.candidates.find((c) => c.participant.id === pid);
        if (!candidate?.eligible) return;
        state.selectedCandidateId = pid;
        state.selectedTradeAssignmentId = null;
        state.preview = deps.engine.previewSwap({
          assignmentId: state.assignmentId,
          newParticipantId: pid,
        });
      }
      rerender();
    });
  });
}

function computeTradePreview(state: PickerState, ctx: ResolvedContext, deps: SwapPickerDeps): SwapPreview | null {
  const tradeId = state.selectedTradeAssignmentId;
  if (!tradeId) return null;
  const other = deps.schedule.assignments.find((a) => a.id === tradeId);
  if (!other) return null;
  // Atomic two-way: source gets other's participant; other gets source's participant.
  return deps.engine.previewSwapChain([
    { assignmentId: state.assignmentId, newParticipantId: other.participantId },
    { assignmentId: other.id, newParticipantId: ctx.sourceParticipant.id },
  ]);
}

function commitSwap(
  state: PickerState,
  ctx: ResolvedContext,
  deps: SwapPickerDeps,
  close: () => void,
  root: HTMLElement,
): void {
  if (state.committing) return;
  if (!state.preview?.valid) return;
  state.committing = true;
  // Clear any inline error left over from a previous failed attempt.
  setSwapError(root, null);

  const preCommitAssignments = deps.schedule.assignments.map((a) => ({ ...a }));

  // Snapshot each checkbox state before close() removes the DOM. Free mode
  // uses only the source checkbox; trade mode reads both sides independently
  // so the user can mark either, both, or neither participant as future-
  // unavailable. Falls back to the session default if a checkbox isn't in
  // the DOM for any reason.
  const recordCbSrc = document.getElementById('swap-picker-record-vacated') as HTMLInputElement | null;
  const recordCbOther = document.getElementById('swap-picker-record-vacated-other') as HTMLInputElement | null;
  const sourceChecked = recordCbSrc ? recordCbSrc.checked : _swapRecordVacatedDefault;
  const otherChecked = recordCbOther ? recordCbOther.checked : _swapRecordVacatedDefault;
  // Reason is captured only in free mode; trade-mode entries deliberately
  // omit `reason` (see rationale on the `reasonRow` block in renderActions).
  const reasonRaw = state.mode === 'free' ? state.reason.trim() : '';
  const reason = reasonRaw === '' ? undefined : reasonRaw;

  let result: ReturnType<SchedulingEngine['swapParticipant']>;
  let label: string;
  let swappedAssignmentIds: string[];
  let recordVacatedSlots: VacatedSlotRecord[] = [];
  if (state.mode === 'trade' && state.selectedTradeAssignmentId) {
    const other = deps.schedule.assignments.find((a) => a.id === state.selectedTradeAssignmentId);
    if (!other) {
      state.committing = false;
      return;
    }
    const otherP = deps.schedule.participants.find((p) => p.id === other.participantId);
    const otherTask = deps.schedule.tasks.find((t) => t.id === other.taskId);
    result = deps.engine.swapParticipantChain([
      { assignmentId: state.assignmentId, newParticipantId: other.participantId },
      { assignmentId: other.id, newParticipantId: ctx.sourceParticipant.id },
    ]);
    label = `החלפה הדדית: ${ctx.sourceParticipant.name} ⇄ ${otherP?.name ?? ''}`;
    swappedAssignmentIds = [state.assignmentId, state.selectedTradeAssignmentId];
    // Per-side opt-in: each checkbox independently controls whether that
    // participant is recorded as unavailable for the slot they vacated.
    if (sourceChecked) {
      recordVacatedSlots.push({
        participantId: ctx.sourceParticipant.id,
        start: ctx.sourceTask.timeBlock.start,
        end: ctx.sourceTask.timeBlock.end,
      });
    }
    if (otherChecked && otherP && otherTask) {
      recordVacatedSlots.push({
        participantId: otherP.id,
        start: otherTask.timeBlock.start,
        end: otherTask.timeBlock.end,
      });
    }
  } else if (state.selectedCandidateId) {
    const inc = deps.schedule.participants.find((p) => p.id === state.selectedCandidateId);
    result = deps.engine.swapParticipant({
      assignmentId: state.assignmentId,
      newParticipantId: state.selectedCandidateId,
    });
    // RTL reading: rightmost name is read first. We put the incoming
    // participant on the right and the outgoing (source) on the left so
    // the arrow reads "incoming ← outgoing" = "incoming replaces outgoing".
    label = `החלפה: ${inc?.name ?? ''} ← ${ctx.sourceParticipant.name}`;
    swappedAssignmentIds = [state.assignmentId];
    if (sourceChecked) {
      recordVacatedSlots = [
        {
          participantId: ctx.sourceParticipant.id,
          start: ctx.sourceTask.timeBlock.start,
          end: ctx.sourceTask.timeBlock.end,
          reason,
        },
      ];
    }
  } else {
    state.committing = false;
    return;
  }

  if (!result.valid) {
    state.committing = false;
    const msgs = result.violations.map((v) => `${violationLabel(v.code)}: ${v.message}`).join('\n');
    // Show the failure INLINE inside the sheet rather than as a toast: a toast
    // (z-index 1300) renders over the bottom sheet (1200) reporting the error,
    // and can be hidden behind the people-picker sheet / FSOS success strip.
    // Inline keeps the reason next to the action; it auto-clears on re-render.
    setSwapError(root, `ההחלפה נכשלה:\n${msgs}`);
    return;
  }

  close();
  deps.onCommit({ label, preCommitAssignments, swappedAssignmentIds, recordVacatedSlots });
}

/**
 * Show or clear an inline error inside the open swap sheet, placed directly
 * above the action buttons. Mirrors the inject modal's inline-error pattern
 * (and the FSOS `.fsos-apply-infeasible` region) instead of a toast — which,
 * at z-index 1300, would render over the sheet (1200) that is reporting the
 * failure. Pass `msg = null` to clear. Any full re-render (e.g. picking a
 * different candidate) rebuilds the body and removes the element naturally.
 */
function setSwapError(root: HTMLElement, msg: string | null): void {
  root.querySelector('.swap-picker-error')?.remove();
  if (!msg) return;
  const actions = root.querySelector('.swap-picker-actions');
  if (!actions) return;
  const el = document.createElement('div');
  el.className = 'swap-picker-error';
  el.setAttribute('role', 'alert');
  el.textContent = msg;
  actions.insertBefore(el, actions.querySelector('.swap-picker-actions-buttons'));
}

/* ──────────────────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────────────────── */

function uniqueGroups(candidates: CandidateEligibility[]): string[] {
  const set = new Set<string>();
  for (const c of candidates) set.add(c.participant.group);
  return [...set].sort();
}

/**
 * Build the corner badge payload from a participant's weekly workload.
 * Displays `effectiveHours` — the same metric the workload sorts use —
 * so the badge always reflects the actual ordering criterion.
 */
function loadBadgeForWorkload(workload: WeeklyWorkload): { text: string; tooltip: string } {
  const eff = workload.effectiveHours.toFixed(1);
  return { text: `${eff}h`, tooltip: `שעות אפקטיביות: ${eff}` };
}

function sortCandidates(
  list: CandidateEligibility[],
  sort: PickerSort,
  workloads: Map<string, WeeklyWorkload>,
): CandidateEligibility[] {
  switch (sort) {
    case 'eligibility':
      return list.sort((a, b) => {
        if (a.eligible !== b.eligible) return a.eligible ? -1 : 1;
        const aw = workloads.get(a.participant.id)?.effectiveHours ?? 0;
        const bw = workloads.get(b.participant.id)?.effectiveHours ?? 0;
        if (aw !== bw) return aw - bw;
        return a.participant.name.localeCompare(b.participant.name, 'he');
      });
    case 'workload-asc':
      return list.sort((a, b) => {
        const aw = workloads.get(a.participant.id)?.effectiveHours ?? 0;
        const bw = workloads.get(b.participant.id)?.effectiveHours ?? 0;
        return aw - bw;
      });
    case 'workload-desc':
      return list.sort((a, b) => {
        const aw = workloads.get(a.participant.id)?.effectiveHours ?? 0;
        const bw = workloads.get(b.participant.id)?.effectiveHours ?? 0;
        return bw - aw;
      });
    case 'name':
      return list.sort((a, b) => a.participant.name.localeCompare(b.participant.name, 'he'));
  }
}

function cleanTaskName(task: Task): string {
  return task.sourceName ?? stripDayPrefix(task.name);
}

function getAffectedParticipants(ctx: ResolvedContext, preview: SwapPreview): Participant[] {
  const set = new Set(preview.affectedParticipantIds);
  return ctx.participants.filter((p) => set.has(p.id));
}

function getParticipantById(ctx: ResolvedContext, id: string): Participant | null {
  return ctx.participants.find((p) => p.id === id) ?? null;
}

/**
 * Mirror of rescue-modal's `computePostSwapTasks`, but driven by
 * `SwapPreview.simulatedAssignments` (already post-swap) instead of by
 * applying a plan's step list. Returns 2 before + reference + 2 after.
 */
function computePostSwapTasksForPreview(
  participantId: string,
  preview: SwapPreview,
  tasks: Task[],
  referenceTaskId: string,
): Array<{ taskName: string; start: Date; end: Date; isReference: boolean }> {
  const taskMap = new Map<string, Task>();
  for (const t of tasks) taskMap.set(t.id, t);

  const myTaskIds = new Set<string>();
  for (const a of preview.simulatedAssignments) {
    if (a.participantId === participantId) myTaskIds.add(a.taskId);
  }

  const resolved: Array<{ taskName: string; start: Date; end: Date; isReference: boolean }> = [];
  for (const tid of myTaskIds) {
    const t = taskMap.get(tid);
    if (!t) continue;
    resolved.push({
      taskName: t.name,
      start: t.timeBlock.start,
      end: t.timeBlock.end,
      isReference: tid === referenceTaskId,
    });
  }
  resolved.sort((a, b) => a.start.getTime() - b.start.getTime());

  // Find the reference; if this participant is losing the slot (outgoing),
  // the reference task won't appear in their post-swap set — fall back to
  // anchoring on the source task's start time.
  let refIdx = resolved.findIndex((t) => t.isReference);
  if (refIdx === -1) {
    const refStart = taskMap.get(referenceTaskId)?.timeBlock.start.getTime();
    if (refStart !== undefined) {
      refIdx = resolved.findIndex((t) => t.start.getTime() >= refStart);
    }
    if (refIdx === -1) refIdx = 0;
  }

  const startIdx = Math.max(0, refIdx - 2);
  const endIdx = Math.min(resolved.length, refIdx + 3);
  return resolved.slice(startIdx, endIdx);
}

function buildSwapParticipantTooltip(
  participantName: string,
  nextTasks: Array<{ taskName: string; start: Date; end: Date; isReference: boolean }>,
  periodStart: Date,
  dayStartHour: number,
): string {
  let html = `<div class="rescue-hover-tt-header">${escHtml(participantName)} — משימות סביב המשבצת אם תוחל ההחלפה</div>`;
  if (nextTasks.length === 0) {
    html += `<div class="rescue-hover-tt-empty">אין משימות קרובות</div>`;
  } else {
    const baseMidnight = new Date(periodStart.getFullYear(), periodStart.getMonth(), periodStart.getDate()).getTime();
    for (let i = 0; i < nextTasks.length; i++) {
      const t = nextTasks[i];
      const shifted = new Date(t.start.getTime());
      if (shifted.getHours() < dayStartHour) shifted.setDate(shifted.getDate() - 1);
      const shiftedMidnight = new Date(shifted.getFullYear(), shifted.getMonth(), shifted.getDate()).getTime();
      const dIdx = Math.floor((shiftedMidnight - baseMidnight) / (24 * 3600 * 1000)) + 1;
      const dayStr = `יום ${dIdx}`;
      const timeStr = `<span dir="ltr">${fmt(t.start)} – ${fmt(t.end)}</span>`;
      const refClass = t.isReference ? ' rescue-hover-tt-task--ref' : '';
      const refMarker = t.isReference ? ' ◄' : '';
      const cleanName = stripDayPrefix(t.taskName);
      html += `<div class="rescue-hover-tt-task${refClass}">${i + 1}. ${escHtml(cleanName)}${refMarker}<span class="rescue-hover-tt-time">${dayStr} ${timeStr}</span></div>`;
    }
  }
  return html;
}
